import { useEffect, useState } from 'react';
import { Content, Title, InputFormTable, Editor, Text } from 'components';
import { Select, Input, Radio, InputNumber, Flex } from 'antd';
import { useProductDispatch, useProductState } from '../context';
import { useUserStack } from 'store';
import { customAlphabet } from 'nanoid';
import { AuditOutlined } from '@ant-design/icons';

import styles from '../styles';
import ProductCategoryForm from './ProductCategoryForm';
import SalesInformation_regularInformation from './SalesInformation_regularInformation';
import SalesInformation_rentalInformation from './SalesInformation_rentalInformation';
import SalesInformation_rentalExplanation from './SalesInformation_rentalExplanation';

/**----------------------------------------
 * [Component] 총관리자: 상품등록 > 판매정보
 * ----------------------------------------
 */
const SalesInformation = ({ defaultData, checkAmount }) => {
  const {
    type,
    shop_information_id,
    product_name,
    product_status,
    is_refund,
    num,
    price,
    tax_type,
    tax_rate,
    product_content,
    is_restocking_notification,
    is_inventory,

    auto_confirm, // 자동확정일
    receive_ids, // 수령지
    regular_information, // 정기배송 정보
    rental_information, // 렌탈 정보
    is_present,
    is_amount,
    product_policy,
    present_information,
  } = useProductState();
  /* ===== INITIAL ===== */

  /* ===== STATE ===== */
  const userInfo = useUserStack();
  const dispatch = useProductDispatch();
  const [calculateAmount, setCalculateAmount] = useState([0, 0]);
  const [checkPresent, setCheckPresent] = useState(false);

  /* ===== FUNCTION ===== */
  /**
   * 할인금/적립금 계산
   * --
   * @param {*} price : 상품금액
   * @param {*} type : 타입
   * @param {*} calculatePrice : 적립/할인금 또는 비율
   * @param {*} calculateType : 적립/할인금 타입
   * @param {*} maxDiscountPrice : 최대 할인 금액
   * @returns
   */
  const handleCalculatePrice = (
    price = 0,
    type = 'DISCOUNT',
    calculatePrice = 0,
    calculateType = 'W',
    maxDiscountPrice = price,
  ) => {
    switch (type) {
      case 'DISCOUNT':
        if (calculateType === 'W') {
          // 원 단위의 할인을 적용
          const validDiscount = Math.min(calculatePrice, maxDiscountPrice);
          const calculatePriceWon = Math.max(price - validDiscount, 0);
          setCalculateAmount([
            Math.floor(calculatePriceWon),
            calculateAmount[1],
          ]);
          return;
        } else if (calculateType === 'R') {
          // 백분율 단위의 할인을 적용
          // const discountPriceRate = price - price * (calculatePrice / 100);
          // const maxRateDiscount = price * (maxDiscountPrice / 100);
          // const validDiscount = Math.min(discountPriceRate, maxRateDiscount);
          // const calculatePriceRate = Math.max(validDiscount, 0);
          const discountPrice = price * (calculatePrice / 100); // 할인 금액 계산
          const validDiscount = Math.min(discountPrice, maxDiscountPrice); // 최대 할인 금액 제한
          const calculatePriceRate = Math.max(price - validDiscount, 0); // 최종 가격 계산
          setCalculateAmount([
            Math.floor(calculatePriceRate),
            calculateAmount[1],
          ]);
          return;
        }
        setCalculateAmount([price, calculateAmount[1]]);
        return;

      case 'RESERVE':
        if (calculateType === 'W') {
          // 원 단위의 적립금을 적용
          setCalculateAmount([calculateAmount[0], calculatePrice]);
          return;
        } else if (calculateType === 'R') {
          // 백분율 단위의 적립금을 적용
          const calculatePriceRate = price * (calculatePrice / 100);
          setCalculateAmount([
            calculateAmount[0],
            Math.floor(calculatePriceRate),
          ]);
          return;
        }
        setCalculateAmount([calculateAmount[0], 0]);
        return;

      default:
        return price;
    }
  };

  /* ===== HOOKS ===== */
  /**
   *
   * --
   */
  useEffect(() => {
    if (defaultData) {
      dispatch({ type: 'CHANGE_TYPE', value: defaultData.type });
      dispatch({
        type: 'CHANGE_PRODUCT_NAME',
        value: defaultData.product_name,
      });
      dispatch({
        type: 'CHANGE_PRDUCT_STATUS',
        value: defaultData.product_status,
      });
      dispatch({
        type: 'CHANGE_PRODUCT_CATEGORY_ID',
        value: defaultData.product_category_id,
      });
      dispatch({ type: 'CHANGE_NUM', value: defaultData.num });
      dispatch({ type: 'CHANGE_PRICE', value: defaultData.price });
      dispatch({ type: 'CHANGE_TAX_TYPE', value: defaultData.tax_type });
      dispatch({ type: 'CHANGE_TAX_RATE', value: defaultData.tax_rate });
      dispatch({
        type: 'CHANGE_PRODUCT_CONTENT',
        value: defaultData.product_content,
      });
      dispatch({ type: 'CHANGE_IS_REFUND', value: defaultData.is_refund });
      dispatch({
        type: 'CHANGE_IS_RESTOCKING_NOTIFICATION',
        value: defaultData.is_restocking_notification,
      });
      dispatch({
        type: 'CHANGE_IS_INVENTORY',
        value: defaultData.is_inventoryn,
      });

      const findGeneralPolicy = defaultData?.product_policy?.find(
        (item) =>
          item?.policy_status === 'POSSIBLE' &&
          item?.is_enable === 'Y' &&
          item?.policy_type === 'GENERAL',
      );
      dispatch({
        type: 'CHANGE_IS_PRODUCT_POLICY',
        value: findGeneralPolicy ?? {},
      });
      dispatch({
        type: 'CHANGE_SHOP_INFORMATION_ID',
        value: defaultData.shop_information_id,
      });
      /* 
      auto_confirm, // 자동확정일
      receive_ids, // 수령지
      regular_information, // 정기배송 정보
      rental_information, // 렌탈 정보
      is_present, //선물하기
      product_policy, //상품 정책
      */
      dispatch({
        type: 'auto_confirm',
        value: defaultData.auto_confirm,
      });
      dispatch({
        type: 'receive_ids',
        value: defaultData.receive_ids,
      });
      dispatch({
        type: 'regular_information',
        value: defaultData.regular_information,
      });
      dispatch({
        type: 'rental_information',
        value: defaultData.rental_information,
      });
      dispatch({
        type: 'is_present',
        value: defaultData.is_present,
      });
      dispatch({
        type: 'present_information',
        value: defaultData.present_information,
      });
      dispatch({
        type: 'is_amount',
        value: defaultData.is_amount,
      });
    }
  }, [defaultData]);

  /**
   *
   * --
   */
  useEffect(() => {
    if (dispatch) {
      const alphabet =
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
      const nanoid = customAlphabet(alphabet, 12);
      dispatch({ type: 'CHANGE_PRODUCT_CODE', value: nanoid() });
    }
  }, []);

  /**
   *
   * --
   */
  useEffect(() => {
    if (userInfo && !defaultData) {
      if (userInfo.shop_information_id !== shop_information_id) {
        dispatch({
          type: 'CHANGE_SHOP_INFORMATION_ID',
          value: userInfo.shop_information_id,
        });
      }
    }
  }, [userInfo, dispatch, shop_information_id, defaultData]);
  /**
   * AMOUNT 일 시 선물 타입 Y
   */
  useEffect(() => {
    if (type === 'AMOUNT') {
      dispatch({ type: 'is_present', value: 'Y' });
    }
  }, [type]);

  /* ===== VARIABLE ===== */
  /**
   *
   * --
   */
  const form = [
    {
      label: '상품유형',
      form: [
        {
          input: (
            <Radio.Group
              value={type}
              onChange={(e) => {
                dispatch({ type: 'CHANGE_TYPE', value: e.target.value });
                checkAmount(e.target.value);
                if (e.target.value === 'AMOUNT') {
                  dispatch({ type: 'is_amount', value: 'Y' });
                  dispatch({ type: 'is_present', value: 'Y' });
                }
              }}
            >
              <Radio value={'DELIVERY'}>일반상품</Radio>
              <Radio value={'RENTAL'}>렌탈상품</Radio>
              <Radio value={'SUBSCRIPTION'}>정기배송</Radio>
              <Radio value={'AMOUNT'}>금액권</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    /* 추가데이터 */
    ...(type === 'DELIVERY' || type === 'AMOUNT'
      ? [
          {
            label: `선물 가능여부`,
            form: [
              {
                input:
                  type === 'AMOUNT' ? (
                    <Radio.Group value='Y' disabled>
                      <Radio value='Y'>선물 가능</Radio>
                    </Radio.Group>
                  ) : (
                    <Radio.Group
                      value={is_present}
                      onChange={(e) => {
                        dispatch({ type: 'is_present', value: e.target.value });
                      }}
                    >
                      <Radio value='Y'>선물 가능</Radio>
                      <Radio value='N'>선물 불가능</Radio>
                    </Radio.Group>
                  ),
              },
            ],
          },
        ]
      : []),
    // ...(is_present === 'Y'
    //   ? [
    //       {
    //         label: `금액권`,
    //         form: [
    //           {
    //             input: (
    //               <Radio.Group value={'Y'}>
    //                 <Radio value="Y">금액권</Radio>
    //               </Radio.Group>
    //             ),
    //           },
    //         ],
    //       },
    //     ]
    //   : []),
    ...((type === 'AMOUNT' || type === 'DELIVERY') && is_present === 'Y'
      ? [
          {
            label: `선물 정보`,
            required: true,
            form: [
              {
                input: (
                  <Flex vertical={true} gap={5}>
                    <Flex gap={3}>
                      <Text>선물 만료일</Text>
                      <InputNumber
                        style={{ width: 150 }}
                        min={0}
                        value={present_information?.present_expired_date}
                        onChange={(value) =>
                          dispatch({
                            type: 'present_information',
                            value: {
                              present_expired_date: value,
                            },
                          })
                        }
                      />
                      <Text>일 뒤 만료</Text>
                      <Text color='#ff0000'>
                        * 선물 한 시간을 기준으로 만료일이 설정됩니다.
                      </Text>
                    </Flex>
                    <Flex gap={3}>
                      <Text>최대연장 횟수</Text>
                      <InputNumber
                        style={{ width: 150 }}
                        min={0}
                        value={present_information?.present_postponed_deadline}
                        onChange={(value) =>
                          dispatch({
                            type: 'present_information',
                            value: {
                              present_postponed_deadline: value,
                            },
                          })
                        }
                      />
                      <Text>번</Text>
                    </Flex>
                  </Flex>
                ),
              },
            ],
          },
        ]
      : []),
    {
      label: '상품명',
      required: true,
      form: [
        {
          input: (
            <Input
              size='large'
              placeholder='예시)[과테말라] 디카페인 200g'
              value={product_name}
              style={{ ...styles.form.input.large, width: 400 }}
              onChange={(e) =>
                dispatch({ type: 'CHANGE_PRODUCT_NAME', value: e.target.value })
              }
              showCount={true}
              maxLength={250}
            />
          ),
        },
      ],
    },
    {
      label: '판매 상태',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={product_status}
                onChange={(e) =>
                  dispatch({
                    type: 'CHANGE_PRDUCT_STATUS',
                    value: e.target.value,
                  })
                }
              >
                <Radio value={'SOLDOUT'}>품절</Radio>
                <Radio value={'NORMAL'}>판매</Radio>
                <Radio value={'STOP'}>판매안함</Radio>
                <Radio value={'WAIT'}>판매대기</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '환불 여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={is_refund}
                onChange={(e) =>
                  dispatch({ type: 'CHANGE_IS_REFUND', value: e.target.value })
                }
              >
                <Radio value={'Y'}>가능</Radio>
                <Radio value={'N'}>불가능</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    {
      label: '상품 분류',
      direction: 'column',
      required: true,
      form: [
        {
          input: (
            <>
              <ProductCategoryForm
                categoryId={defaultData?.product_category_id}
                onSelectCategory={(categoryId) =>
                  dispatch({
                    type: 'CHANGE_PRODUCT_CATEGORY_ID',
                    value: categoryId,
                  })
                }
              />
            </>
          ),
          fullscreen: true,
        },
      ],
    },
    {
      label: '상품 수량',
      required: true,
      form: [
        {
          input: (
            <>
              <InputNumber
                style={{ width: 150 }}
                min={0}
                value={num}
                onChange={(value) =>
                  dispatch({ type: 'CHANGE_NUM', value: value })
                }
              />
              <span>EA</span>
            </>
          ),
        },
      ],
    },
    {
      label: '상품 금액',
      required: true,
      form: [
        {
          input: (
            <>
              <InputNumber
                style={{ width: 150 }}
                min={0}
                value={price}
                formatter={(value) =>
                  `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value.replace(/\₩\s?|(,*)/g, '')}
                onChange={(value) =>
                  dispatch({ type: 'CHANGE_PRICE', value: value })
                }
              />
              <span>원</span>
            </>
          ),
        },
      ],
    },
    ...(type === 'DELIVERY'
      ? [
          {
            label: '할인/적립 설정',
            form: [
              {
                input: (
                  <div>
                    {/* 할인금 */}
                    <div style={{ marginBottom: '16px' }}>
                      <Flex vertical='true' gap={10}>
                        <Flex justify='' gap={5} align='center'>
                          <p style={{ fontWeight: 'bold' }}>할인</p>
                          <InputNumber
                            style={{ width: 150 }}
                            value={product_policy.discount_rate}
                            placeholder='할인금을 입력하세요.'
                            min={0}
                            max={product_policy.discount_type === 'R' && 100}
                            parser={(value) => value.replace(/\₩\s?|(,*)/g, '')}
                            formatter={(value) =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            onChange={(value) => {
                              dispatch({
                                type: 'CHANGE_IS_PRODUCT_POLICY',
                                value: { discount_rate: value },
                              });
                              handleCalculatePrice(
                                price,
                                'DISCOUNT',
                                value,
                                product_policy?.discount_type,
                                product_policy?.max_discount_price,
                              );
                            }}
                          />
                          <Select
                            style={{ width: 80 }}
                            value={product_policy.discount_type}
                            onChange={(value) => {
                              dispatch({
                                type: 'CHANGE_IS_PRODUCT_POLICY',
                                value: { discount_rate: 0 },
                              });
                              dispatch({
                                type: 'CHANGE_IS_PRODUCT_POLICY',
                                value: { discount_type: value },
                              });
                              handleCalculatePrice(
                                price,
                                'DISCOUNT',
                                0,
                                product_policy?.discount_type,
                                product_policy?.max_discount_price,
                              );
                            }}
                            options={[
                              { label: '원', value: 'W' },
                              { label: '%', value: 'R' },
                            ]}
                          />
                          <p style={{ fontWeight: 'bold', marginLeft: '5px' }}>
                            최대 할인금
                          </p>
                          <InputNumber
                            style={{ width: 150 }}
                            value={product_policy.max_discount_price}
                            placeholder='최대 할인금을 입력하세요.'
                            min={0}
                            maxLength={11}
                            parser={(value) => value.replace(/\₩\s?|(,*)/g, '')}
                            formatter={(value) =>
                              `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            onChange={(value) => {
                              dispatch({
                                type: 'CHANGE_IS_PRODUCT_POLICY',
                                value: { max_discount_price: value },
                              });
                              handleCalculatePrice(
                                price,
                                'DISCOUNT',
                                product_policy?.discount_rate,
                                product_policy?.discount_type,
                                value,
                              );
                            }}
                          />
                          원
                          <Text color='#ff0000'>
                            * 최대 할인금이 없을 경우 할인이 적용되지 않습니다.
                          </Text>
                        </Flex>
                      </Flex>
                    </div>

                    {/* 적립금 */}
                    <div>
                      <Flex vertical='true' gap={10}>
                        <Flex justify='' gap={5} align='center'>
                          <p style={{ fontWeight: 'bold' }}>적립</p>
                          <InputNumber
                            style={{ width: 150 }}
                            value={product_policy.reserve_rate}
                            placeholder='적립금을 입력하세요.'
                            min={0}
                            max={product_policy.reserve_type === 'R' && 100}
                            parser={(value) => value.replace(/\₩\s?|(,*)/g, '')}
                            formatter={(value) =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            onChange={(value) => {
                              dispatch({
                                type: 'CHANGE_IS_PRODUCT_POLICY',
                                value: { reserve_rate: value },
                              });
                              handleCalculatePrice(
                                calculateAmount[0],
                                'RESERVE',
                                value,
                                product_policy?.reserve_type,
                              );
                            }}
                          />
                          <Select
                            style={{ width: 80 }}
                            value={product_policy.reserve_type}
                            onChange={(value) => {
                              dispatch({
                                type: 'CHANGE_IS_PRODUCT_POLICY',
                                value: { reserve_rate: 0 },
                              });
                              dispatch({
                                type: 'CHANGE_IS_PRODUCT_POLICY',
                                value: { reserve_type: value },
                              });
                            }}
                            options={[
                              { label: '원', value: 'W' },
                              { label: '%', value: 'R' },
                            ]}
                          />
                        </Flex>
                      </Flex>
                    </div>
                  </div>
                ),
              },
            ],
          },
          {
            label: '할인/적립 설정',
            form: [
              {
                input: (
                  <div>
                    <div>
                      <Flex vertical='true' gap={10}>
                        <Flex justify='' gap={5} align='center'>
                          <p style={{ fontWeight: 'bold' }}>
                            할인 후 적용 금액
                          </p>
                          <InputNumber
                            disabled={true}
                            style={{ width: 150 }}
                            value={calculateAmount[0]}
                            parser={(value) => value.replace(/\₩\s?|(,*)/g, '')}
                            formatter={(value) =>
                              `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                          />
                          <p style={{ fontWeight: 'bold', marginLeft: '5px' }}>
                            적립 적용 금액
                          </p>
                          <InputNumber
                            disabled={true}
                            style={{ width: 150 }}
                            value={calculateAmount[1]}
                            parser={(value) => value.replace(/\₩\s?|(,*)/g, '')}
                            formatter={(value) =>
                              `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                          />
                        </Flex>
                      </Flex>
                    </div>
                  </div>
                ),
              },
            ],
          },
        ]
      : []),

    {
      label: '상품 코드',
      form: [
        {
          input: (
            <>
              {defaultData ? (
                <p>{defaultData.product_code}</p>
              ) : (
                <p>자동생성</p>
              )}
            </>
          ),
        },
      ],
    },
    {
      label: '과세 방식',
      form: [
        {
          input: (
            <>
              <Select
                style={{ width: 150 }}
                value={tax_type}
                onChange={(value) =>
                  dispatch({ type: 'CHANGE_TAX_TYPE', value: value })
                }
                options={[
                  { label: '과세', value: 'TAXABLE' },
                  { label: '면세', value: 'EXEMPT' },
                  { label: '영세', value: 'ZERO_RATED' },
                ]}
              />
            </>
          ),
        },
      ],
    },
    {
      label: '과세율',
      form: [
        {
          input: (
            <>
              <InputNumber
                style={{ width: 150 }}
                value={tax_rate}
                min={0}
                max={100}
                formatter={(value) => `${value}%`}
                parser={(value) => value?.replace('%', '')}
                onChange={(value) =>
                  dispatch({ type: 'CHANGE_TAX_RATE', value: value })
                }
              />
            </>
          ),
        },
      ],
    },
    {
      label: '재입고 알림 여부',
      form: [
        {
          input: (
            <>
              <Radio.Group
                value={is_restocking_notification}
                onChange={(e) =>
                  dispatch({
                    type: 'CHANGE_IS_RESTOCKING_NOTIFICATION',
                    value: e.target.value,
                  })
                }
              >
                <Radio value={'Y'}>사용</Radio>
                <Radio value={'N'}>미사용</Radio>
              </Radio.Group>
            </>
          ),
        },
      ],
    },
    // {
    //   label: '재고 관리 여부',
    //   form: [
    //     {
    //       input: (
    //         <>
    //           <Radio.Group
    //             value={is_inventory}
    //             onChange={(e) =>
    //               dispatch({
    //                 type: 'CHANGE_IS_INVENTORY',
    //                 value: e.target.value,
    //               })
    //             }
    //           >
    //             <Radio value={'Y'}>사용</Radio>
    //             <Radio value={'N'}>미사용</Radio>
    //           </Radio.Group>
    //         </>
    //       ),
    //     },
    //   ],
    // },
    {
      label: '상품내용',
      form: [
        {
          input: (
            <div style={{ width: '850px', height: '430px' }}>
              <Editor
                height={430}
                initialContent={product_content}
                editorOnChange={(content) =>
                  dispatch({ type: 'CHANGE_PRODUCT_CONTENT', value: content })
                }
              />
            </div>
          ),
        },
      ],
    },

    /* === 25.02.24 추가데이터 === */
    // 자동확정일
    {
      label: '자동구매확정일',
      form: [
        {
          input: (
            <>
              <Select
                style={{ width: 150 }}
                value={auto_confirm}
                onChange={(value) =>
                  dispatch({ type: 'auto_confirm', value: value })
                }
                options={[
                  { label: '자동확정', value: 0 },
                  { label: '1일 후', value: 1 },
                  { label: '3일 후', value: 3 },
                  { label: '5일 후', value: 5 },
                  { label: '7일 후', value: 7 },
                  { label: '10일 후', value: 10 },
                ]}
              />
            </>
          ),
        },
      ],
    },

    // // 스토어픽업장소
    // {
    //   label: `스토어픽업장소`,
    //   form: [
    //     {
    //       input: (
    //         <SalesInformation_storeInformation
    //           defaultValue={receive_ids}
    //           onChange={(value) => dispatch({ type: 'receive_ids', value })}
    //         />
    //       ),
    //     },
    //   ],
    // },

    // 정기배송정보
    ...(type === 'SUBSCRIPTION'
      ? [
          {
            label: `정기배송정보`,
            required: true,
            form: [
              {
                input: (
                  <SalesInformation_regularInformation
                    defaultValue={regular_information}
                    onChange={(value) =>
                      dispatch({ type: 'regular_information', value })
                    }
                  />
                ),
              },
            ],
          },
        ]
      : []),

    // 렌탈정보
    ...(type === 'RENTAL'
      ? [
          {
            label: '렌탈정보',
            required: true,
            form: [
              {
                style: { width: '100%' },
                input: (
                  <SalesInformation_rentalInformation
                    defaultData={rental_information}
                    onChange={(value) =>
                      dispatch({ type: 'rental_information', value })
                    }
                  />
                ),
              },
            ],
          },
          {
            label: '렌탈부가설명',
            form: [
              {
                style: { width: '100%' },
                input: (
                  <SalesInformation_rentalExplanation
                    defaultData={rental_information}
                    onChange={(value) =>
                      dispatch({ type: 'rental_information', value })
                    }
                  />
                ),
              },
            ],
          },
        ]
      : []),
  ]; // :end

  /* ===== RENDER ===== */
  return (
    <Content
      maxWidth={'100%'}
      padding={20}
      borderRadius={4}
      style={{ marginBottom: 25 }}
      className='boxShadow'
    >
      <Title size={5} align='left' style={{ marginBottom: 10 }}>
        <AuditOutlined /> 판매 정보
      </Title>
      <InputFormTable form={form} />
    </Content>
  );
};

export default SalesInformation;
